import { createSlice } from '@reduxjs/toolkit';
import { useSelector, useDispatch } from 'react-redux';
import {
  EditProfileViewApi,
  EditProfileView_extrareducer,
  ViewDetailsApi,
  ViewDetails_extrareducer,
  SubmitProfileDetailsApi,
  SubmitProfile_extrareducer,
  MobileUpdateApi,
  MobileUpdate_extrareducer,
} from './edit-profile/index';

const initialState = {
  isInitialRender: true,
  otpCode: null,
  isAddrOTPVerified: null,
  isCompanyOTPVerified: null,
  isBankOTPVerified: null,
  isOtpDrawerOpened: null,
  isMobileOTPVerified: null,
  isEmailOTPVerified: null,
  newMobileNumber: null,
  newEmail: null,
  addressDetails: {
    addressLine1: null,
    addressLine2: null,
    landmark: null,
    city: null,
    state: null,
    pincode: null,
  },
  companyDetails: {
    companyName: null,
    officeArea: null,
    officeCity: null,
    officePincode: null,
    designation: null,
  },
  bankDetails: {
    accountNumber: null,
    ifscCode: null,
    bankName: null,
    bankBranch: null,
  },
  contactDetails: {
    mobileNumber: null,
    emailId: null,
  },
  editProfileDetailsApiState: {
    response: {},
    isLoading: false,
    isError: false,
    isSuccess: false,
  },
  viewDetailsApiState: {
    response: {},
    isLoading: false,
    isError: false,
    isSuccess: false,
  },
  submitProfileDetailsApiState: {
    response: {},
    isLoading: false,
    isError: false,
    isSuccess: false,
  },
  mobileUpdateApiState: {
    response: {},
    isLoading: false,
    isError: false,
    isSuccess: false,
  },
};

export const editProfileSlice = createSlice({
  name: 'editProfile',
  initialState,
  reducers: {
    updateInitialRender: (state) => {
      state.isInitialRender = false;
    },
    updateOtp: (state, action) => {
      state.otpCode = action.payload;
    },
    updateMobileOtpVerified: (state, action) => {
      state.isMobileOTPVerified = action.payload;
    },
    updateEmailOtpVerified: (state, action) => {
      state.isEmailOTPVerified = action.payload;
    },
    updateAddrOtpVerified: (state, action) => {
      state.isAddrOTPVerified = action.payload;
    },
    updateCompanyOtpVerified: (state, action) => {
      state.isCompanyOTPVerified = action.payload;
    },
    updateBankOtpVerified: (state, action) => {
      state.isBankOTPVerified = action.payload;
    },
    updateOtpDrawerOpened: (state, action) => {
      state.isOtpDrawerOpened = action.payload;
    },
    updateAddressDetails: (state, action) => {
      state.addressDetails = action.payload;
    },
    updateCompanyDetails: (state, action) => {
      state.companyDetails = action.payload;
    },
    updateBankDetails: (state, action) => {
      state.bankDetails = action.payload;
    },
    updateContactDetails: (state, action) => {
      state.contactDetails = action.payload;
    },
    updateMobileNumber: (state, action) => {
      state.newMobileNumber = action.payload;
    },
    updateEmail: (state, action) => {
      state.newEmail = action.payload;
    },
    resetSubmitProfileDetailsApiState: (state) => {
      state.submitProfileDetailsApiState = {
        response: {},
        isLoading: false,
        isError: false,
        isSuccess: false,
      };
    },
  },
  extraReducers: {
    ...EditProfileView_extrareducer,
    ...ViewDetails_extrareducer,
    ...SubmitProfile_extrareducer,
    ...MobileUpdate_extrareducer,
  },
});

export function useEditProfileStore() {
  const isInitialRender = useSelector(
    (state) => state.editProfile.isInitialRender
  );
  const otpCode = useSelector((state) => state.editProfile.otpCode);
  const isMobileOTPVerified = useSelector(
    (state) => state.editProfile.isMobileOTPVerified
  );
  const isEmailOTPVerified = useSelector(
    (state) => state.editProfile.isEmailOTPVerified
  );
  const isAddrOTPVerified = useSelector(
    (state) => state.editProfile.isAddrOTPVerified
  );
  const isCompanyOTPVerified = useSelector(
    (state) => state.editProfile.isCompanyOTPVerified
  );
  const isBankOTPVerified = useSelector(
    (state) => state.editProfile.isBankOTPVerified
  );
  const isOtpDrawerOpened = useSelector(
    (state) => state.editProfile.isOtpDrawerOpened
  );
  const addressDetails = useSelector(
    (state) => state.editProfile.addressDetails
  );
  const companyDetails = useSelector(
    (state) => state.editProfile.companyDetails
  );
  const bankDetails = useSelector((state) => state.editProfile.bankDetails);
  const contactDetails = useSelector(
    (state) => state.editProfile.contactDetails
  );
  const newMobileNumber = useSelector(
    (state) => state.editProfile.newMobileNumber
  );
  const newEmail = useSelector((state) => state.editProfile.newEmail);
  const editProfileDetailsApiState = useSelector(
    (state) => state.editProfile.editProfileDetailsApiState
  );
  const viewDetailsApiState = useSelector(
    (state) => state.editProfile.viewDetailsApiState
  );
  const submitProfileDetailsApiState = useSelector(
    (state) => state.editProfile.submitProfileDetailsApiState
  );
  const mobileUpdateApiState = useSelector(
    (state) => state.editProfile.mobileUpdateApiState
  );

  const dispatch = useDispatch();

  return {
    // state
    isInitialRender,
    otpCode,
    isMobileOTPVerified,
    isEmailOTPVerified,
    isAddrOTPVerified,
    isCompanyOTPVerified,
    isBankOTPVerified,
    isOtpDrawerOpened,
    newMobileNumber,
    newEmail,
    addressDetails,
    companyDetails,
    bankDetails,
    contactDetails,
    editProfileDetailsApiState,
    viewDetailsApiState,
    submitProfileDetailsApiState,
    mobileUpdateApiState,
    // reducers
    updateInitialRender: () => dispatch(updateInitialRender()),
    updateOtp: (payload) => dispatch(updateOtp(payload)),
    updateMobileOtpVerified: (payload) =>
      dispatch(updateMobileOtpVerified(payload)),
    updateEmailOtpVerified: (payload) =>
      dispatch(updateEmailOtpVerified(payload)),
    updateAddrOtpVerified: (payload) =>
      dispatch(updateAddrOtpVerified(payload)),
    updateCompanyOtpVerified: (payload) =>
      dispatch(updateCompanyOtpVerified(payload)),
    updateBankOtpVerified: (payload) =>
      dispatch(updateBankOtpVerified(payload)),
    updateOtpDrawerOpened: (payload) =>
      dispatch(updateOtpDrawerOpened(payload)),
    updateAddressDetails: (payload) => dispatch(updateAddressDetails(payload)),
    updateCompanyDetails: (payload) => dispatch(updateCompanyDetails(payload)),
    updateBankDetails: (payload) => dispatch(updateBankDetails(payload)),
    updateContactDetails: (payload) => dispatch(updateContactDetails(payload)),
    updateMobileNumber: (payload) => dispatch(updateMobileNumber(payload)),
    updateEmail: (payload) => dispatch(updateEmail(payload)),
    resetSubmitProfileDetailsApiState: () =>
      dispatch(resetSubmitProfileDetailsApiState()),
    // api calls
    EditProfileViewApi: (payload) => dispatch(EditProfileViewApi(payload)),
    ViewDetailsApi: (payload) => dispatch(ViewDetailsApi(payload)),
    SubmitProfileDetailsApi: (payload) =>
      dispatch(SubmitProfileDetailsApi(payload)),
    MobileUpdateApi: (payload) => dispatch(MobileUpdateApi(payload)),
  };
}

export const {
  updateInitialRender,
  updateOtp,
  updateMobileOtpVerified,
  updateEmailOtpVerified,
  updateAddrOtpVerified,
  updateCompanyOtpVerified,
  updateBankOtpVerified,
  updateOtpDrawerOpened,
  updateAddressDetails,
  updateBankDetails,
  updateContactDetails,
  updateCompanyDetails,
  updateMobileNumber,
  updateEmail,
  resetSubmitProfileDetailsApiState,
} = editProfileSlice.actions;

export default editProfileSlice.reducer;
